/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import getConfig from '../../config';
import { getLogger } from '../../utils/SearchLogger';
import uuid from 'uuid/v4';
import Request from '../../utils/Request';
import PerformanceUtils from '../../utils/PerformanceUtils';
import GeoService from '../GeoService';

export default async ({
	searchQuery,
	searchHostContext,
}: {
	searchQuery: string;
	searchHostContext: HostContext;
}): Promise<unknown> => {
	try {
		const geoService = new GeoService(searchHostContext.environment);
		const geoData = await geoService.fetchGeoData();

		let sanitizeInput = searchQuery;
		if (typeof String.prototype.normalize === 'function') {
			// normalize() exists, use it
			sanitizeInput = sanitizeInput.normalize();
		}
		sanitizeInput = sanitizeInput.toLocaleLowerCase();

		const hostname = getConfig(searchHostContext.environment, 'SEARCH_SERVER');
		const path = '/v1/users/me/searches';
		const curRequestId = uuid();
		const startTime = Date.now();
		const surface = searchHostContext.hostInfo.surface;
		const client = searchHostContext.hostInfo.client;

		const headers = {
			'Content-Type': 'application/json',
			'x-api-key': searchHostContext.hostInfo.apiKey,
			authorization: `Bearer ${searchHostContext.imsInfo.accessToken}`,
			'x-request-id': curRequestId,
		};

		const bodyData = {
			surface,
			client,
			query: sanitizeInput,
			address: {
				country: geoData.country,
				state: geoData.state,
			},
		};
		const putData = JSON.stringify(bodyData);

		const historyResponse = await Request.withPayload(putData)
			.withHeaders(headers)
			.send('PUT', `https://${hostname}${path}`);

		if (historyResponse.isError()) {
			const loggerData = {
				url: `https://${hostname}${path}`,
				status: historyResponse.status(),
				message: historyResponse.statusText(),
				requestId: curRequestId,
				duration: Date.now() - startTime,
				performanceTiming: PerformanceUtils.getPerformanceTimingByName(historyResponse.requestUrl()),
			};
			getLogger().error('Search Service', 'Problem with Search History put request', loggerData);
			const searchServiceError = new Error('Push history error');
			throw searchServiceError;
		}
		return historyResponse.data();
	} catch (e) {
		getLogger().error('Search Service', 'Problem with Search History push request', e);
		const searchServiceError = new Error('Push history error');
		throw searchServiceError;
	}
};
